import { useState } from 'react';

import { useModalFocus } from '../lib/useModalFocus.js';

export interface SelectableScene {
  readonly id: string;
  /** The screenplay's own number ("14A") — not sortable, not stable. */
  readonly sceneNumber: string | null;
  readonly slugline: string | null;
}

/**
 * Adding a location the screenplay implies but the breakdown missed.
 *
 * THIS IS THE FALSE-NEGATIVE SURFACE, and it is the one kind of model error
 * that leaves no trace anywhere else. A wrong requirement can be deleted and a
 * vague one can be edited, so both are recoverable from the row itself; a
 * MISSING one has no row to have been wrong. If it is not captured at the
 * moment a location manager types it, the fact that the model never proposed it
 * is gone for good — and "what did it miss" is the question a better prompt is
 * trying to answer.
 *
 * MANY SCENES AT ONCE, because a location requirement is shared by
 * construction: "Hotel room" is scenes 3, 8, 14 and 21, and the producer adding
 * it knows all four right now. Collecting them one at a time would either lose
 * the grouping or create four requirements that then have to be merged back.
 *
 * NO VALUES HERE. The dialog takes a name and a scope and stops. Spaces, visual
 * attributes, physical features and conditions are filled in on the card, where
 * the screenplay is visible beside them — asking for them in a modal would mean
 * answering "what must this place be like?" with the text out of sight.
 */
export function AddRequirementDialog({
  scenes,
  preselectedSceneIds = [],
  busy = false,
  error = null,
  onCancel,
  onSubmit,
}: {
  scenes: readonly SelectableScene[];
  /**
   * The scene the producer was already looking at when they asked for this.
   *
   * Opening with nothing chosen made the dialog's first job "find the scene you
   * are on in a list of every scene in the screenplay", which on a feature is a
   * hundred and twenty rows. The choice stays editable — a location is often
   * shared — this only starts from where the request came from.
   */
  preselectedSceneIds?: readonly string[];
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  onSubmit: (input: { title: string; sceneIds: readonly string[] }) => void;
}) {
  const [title, setTitle] = useState('');
  const [selected, setSelected] = useState<readonly string[]>(preselectedSceneIds);
  const modal = useModalFocus<HTMLDivElement>(() => {
    if (!busy) onCancel();
  });

  const toggle = (sceneId: string) => {
    setSelected((current) =>
      current.includes(sceneId) ? current.filter((id) => id !== sceneId) : [...current, sceneId],
    );
  };

  const trimmed = title.trim();
  // A requirement with no name is not a requirement. The scene scope, by
  // contrast, is legitimately empty: a unit base or a stage belongs to the
  // production rather than to any scene.
  const submittable = trimmed !== '' && !busy;

  return (
    <div
      data-testid="add-requirement-dialog"
      onKeyDown={modal.onKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label="Add requirement"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(35, 35, 35, 0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '5vh 16px',
        overflowY: 'auto',
        zIndex: 40,
      }}
      onClick={(event) => {
        // Only a click on the backdrop itself dismisses; a click that started
        // inside the card and drifted out must not throw away typed work.
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div
        ref={modal.ref}
        style={{
          background: 'var(--color-bg)',
          width: 'min(640px, 100%)',
          border: '1px solid var(--color-divider)',
          boxShadow: '0 18px 48px rgba(16, 24, 40, 0.18)',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 12,
            flexWrap: 'wrap',
            padding: '18px 22px',
            borderBottom: '2px solid var(--ink)',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: 'var(--font-heading)',
              fontSize: 22,
              letterSpacing: '0.02em',
            }}
          >
            ADD REQUIREMENT
          </h2>
          <span className="mm" style={{ fontSize: 13 }}>
            A location the script implies but the breakdown missed
          </span>
          <button
            type="button"
            className="ctl"
            data-testid="add-requirement-close"
            aria-label="Cancel"
            disabled={busy}
            onClick={onCancel}
            style={{ marginLeft: 'auto' }}
          >
            ×
          </button>
        </header>

        <div style={{ padding: '18px 22px' }}>
          <label
            className="flbl"
            htmlFor="requirement-name"
            style={{ display: 'block', marginBottom: 6 }}
          >
            Requirement name
          </label>
          <input
            id="requirement-name"
            data-testid="add-requirement-name"
            value={title}
            disabled={busy}
            placeholder="e.g. Ferry deck"
            maxLength={255}
            onChange={(event) => setTitle(event.target.value)}
            style={{
              width: '100%',
              padding: '11px 12px',
              border: '2px solid var(--ink)',
              font: 'inherit',
              background: 'var(--color-bg)',
            }}
          />

          <p className="flbl" style={{ margin: '18px 0 8px' }}>
            Which scenes use it?
          </p>
          <div
            data-testid="add-requirement-scenes"
            style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}
          >
            {scenes.map((option) => {
              const on = selected.includes(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  className="ctl"
                  data-testid={`add-requirement-scene-${option.id}`}
                  data-on={on ? '1' : '0'}
                  aria-pressed={on}
                  disabled={busy}
                  onClick={() => toggle(option.id)}
                  style={{ display: 'inline-flex', gap: 6, alignItems: 'baseline' }}
                >
                  <span style={{ fontWeight: 700 }}>
                    Sc {option.sceneNumber ?? '—'}
                  </span>
                  <span className="mm" style={{ fontSize: 12 }}>
                    {option.slugline ?? 'Untitled'}
                  </span>
                </button>
              );
            })}
          </div>

          <p
            style={{
              marginTop: 18,
              padding: '12px 14px',
              background: 'var(--color-surface)',
              border: '1px solid var(--color-divider)',
              fontSize: 13,
              lineHeight: 1.6,
            }}
          >
            <strong>Values are added on the card.</strong>{' '}
            <span className="mm">
              The new requirement arrives with every category — location type, spaces, environment,
              physical features, conditions — ready for you to fill in with “+ Add”. Every category
              is listed including the empty ones, because an empty one is a statement that this
              location needs none.
            </span>
          </p>

          {error === null ? null : (
            <p
              data-testid="add-requirement-error"
              style={{ marginTop: 12, fontSize: 13, color: 'var(--danger)' }}
            >
              {error}
            </p>
          )}
        </div>

        <footer
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            padding: '14px 22px',
            borderTop: '1px solid var(--color-divider)',
            background: 'var(--color-surface)',
          }}
        >
          {/* Said plainly, because it changes what the row MEANS: a manual
              requirement carries human provenance and is never removed by a
              later re-extraction, unlike one the model proposed. */}
          <span className="mm" style={{ fontSize: 12 }}>
            Added manually — flagged as human-confirmed, not AI-inferred.
          </span>
          <button
            type="button"
            className="btn btn-ghost"
            data-testid="add-requirement-cancel"
            disabled={busy}
            onClick={onCancel}
            style={{ marginLeft: 'auto' }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            data-testid="add-requirement-submit"
            disabled={!submittable}
            onClick={() => onSubmit({ title: trimmed, sceneIds: selected })}
          >
            {busy ? 'Adding…' : 'Add requirement'}
          </button>
        </footer>
      </div>
    </div>
  );
}
